import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Lock, Mail, ShieldCheck, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SecuritySettings() {
  const [profile, setProfile] = useState(null);
  const [pwd, setPwd] = useState({ actual: '', nueva: '', confirmar: '' });
  const [emailForm, setEmailForm] = useState({ nuevo_email: '', password: '' });
  const [savingPwd, setSavingPwd] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const fetchMe = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setProfile(r.data);
    } catch { /* silencioso */ }
  };
  useEffect(() => { fetchMe(); }, []);
  const onUpdated = fetchMe;

  const cambiarPassword = async (e) => {
    e.preventDefault();
    if (pwd.nueva.length < 8) { toast.error('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (pwd.nueva !== pwd.confirmar) { toast.error('Las contraseñas no coinciden'); return; }
    setSavingPwd(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/change-password`,
        { password_actual: pwd.actual, password_nueva: pwd.nueva }, { withCredentials: true });
      toast.success(r.data.message);
      setPwd({ actual: '', nueva: '', confirmar: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setSavingPwd(false);
    }
  };

  const cambiarEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/change-email`, emailForm, { withCredentials: true });
      toast.success(r.data.message, { duration: 8000 });
      if (r.data.email_enviado === false) {
        toast.warning('El correo no pudo enviarse automáticamente. Contacta con Manklar para completar el cambio.', { duration: 8000 });
      }
      setEmailForm({ nuevo_email: '', password: '' });
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al solicitar el cambio de correo');
    } finally {
      setSavingEmail(false);
    }
  };

  const inputCls = "h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600 rounded-xl";

  return (
    <div className="p-6 rounded-2xl mb-6" style={{ backgroundColor: 'rgba(10,10,10,0.8)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="security-settings">
      <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-emerald-400" />Seguridad de la cuenta
      </h3>
      <div className="flex items-center gap-2 mb-6">
        {profile && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${
            profile.email_verificado
              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
              : 'bg-amber-500/10 text-amber-400 border-amber-500/25'
          }`} data-testid="email-verificado-badge">
            {profile.email_verificado ? 'Correo verificado' : 'Correo pendiente de verificar'}
          </span>
        )}
        {profile?.pending_email && (
          <span className="text-[10px] text-zinc-500">Cambio pendiente: {profile.pending_email}</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <form onSubmit={cambiarPassword} className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Cambiar contraseña</p>
          <Input type="password" placeholder="Contraseña actual" value={pwd.actual} required
            onChange={(e) => setPwd((p) => ({ ...p, actual: e.target.value }))} className={inputCls} data-testid="pwd-actual-input" />
          <Input type="password" placeholder="Nueva contraseña (mín. 8)" value={pwd.nueva} required
            onChange={(e) => setPwd((p) => ({ ...p, nueva: e.target.value }))} className={inputCls} data-testid="pwd-nueva-input" />
          <Input type="password" placeholder="Repite la nueva contraseña" value={pwd.confirmar} required
            onChange={(e) => setPwd((p) => ({ ...p, confirmar: e.target.value }))} className={inputCls} data-testid="pwd-confirmar-input" />
          <Button type="submit" disabled={savingPwd} variant="outline"
            className="w-full h-10 border-white/20 text-white rounded-xl" data-testid="pwd-guardar-btn">
            {savingPwd ? 'Guardando...' : 'Actualizar contraseña'}
          </Button>
        </form>

        <form onSubmit={cambiarEmail} className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />Cambiar correo electrónico</p>
          <Input type="email" placeholder="Nuevo correo electrónico" value={emailForm.nuevo_email} required
            onChange={(e) => setEmailForm((f) => ({ ...f, nuevo_email: e.target.value }))} className={inputCls} data-testid="email-nuevo-input" />
          <Input type="password" placeholder="Tu contraseña actual" value={emailForm.password} required
            onChange={(e) => setEmailForm((f) => ({ ...f, password: e.target.value }))} className={inputCls} data-testid="email-password-input" />
          <div className="flex items-start gap-2 text-[10px] text-zinc-600">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            El nuevo correo deberá verificarse antes de quedar activo. Hasta entonces sigues accediendo con el actual.
          </div>
          <Button type="submit" disabled={savingEmail} variant="outline"
            className="w-full h-10 border-white/20 text-white rounded-xl" data-testid="email-guardar-btn">
            {savingEmail ? 'Enviando...' : 'Solicitar cambio de correo'}
          </Button>
        </form>
      </div>
    </div>
  );
}
